/**
 * Admin Console — Impact Activities (DEV-1480).
 *
 * The full activity catalog: 117 rows grouped dimension × rung. Six are live;
 * 111 are drafts imported from Life4Earth_Activity_Catalog.xlsx.
 *
 * ── THE ONE THING TO UNDERSTAND ─────────────────────────────────────────────
 * A draft CANNOT be enabled. Not "shouldn't" — cannot. The emission engine
 * detects each act with hand-written C# (does this user have an active 'home'
 * membership? a photo and a bio?). A catalog row carries the weights and the
 * copy; it does not carry the detector. So an activity with no emission_binding
 * has no code that can ever notice it happening, and enabling it would put a
 * prompt in front of a member promising credit that never arrives — the exact
 * failure this whole ticket exists to kill.
 *
 * The DB enforces it (chk_ia_enabled_requires_binding). This page therefore shows
 * unwired rows with a DISABLED toggle and states the earning path — "needs a fact
 * detector in ImpactEmission" — rather than letting an admin click it and eat an
 * error. That is CLAUDE.md's gated-affordance principle: disable-and-explain,
 * never enabled-then-fail.
 *
 * Turning an activity OFF stops future emissions and hides its member prompts. It
 * NEVER revokes already-emitted impact_events. Medals earned stay earned.
 *
 * English-only: platform-level system administration is not localized (CLAUDE.md).
 * The member-facing display_title / display_hint ARE localized, via the
 * translations JSONB — edit those here, and members see them in their language.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../api/client';
import { invalidate } from '../../api/queryCache';
import styles from './AdminImpactActivities.module.css';

/* ── Types (mirror ImpactActivityDto / AdminImpactCatalogResponse) ── */

type DimKey = 'mind' | 'voice' | 'body' | 'heart' | 'wings' | 'roots';
type Rung = 'explorer' | 'contributor' | 'champion' | 'governor';

interface AdminActivity {
  activityKey: string;
  dimension: DimKey;
  rung: Rung;
  displayTitle: string;
  displayHint: string | null;
  enabled: boolean;
  emissionBinding: string | null;
  isWired: boolean;
  repeatPolicy: string;
  dimensionWeights: string;
  magnitude: number;
  isSeeded: boolean;
  sourceNote: string | null;
  enabledChangedAt: string | null;
  enabledChangedByUserGuid: string | null;
  /** DEV-1489 — has any impact_events referenced this key. Gates hard delete. */
  hasEmitted: boolean;
}

/** DEV-1489 — a new activity is deletable only if it is an admin draft that has
 *  never emitted. Everything else is disable-only (medals stay earned). */
function isHardDeletable(a: AdminActivity): boolean {
  return !a.isSeeded && !a.hasEmitted;
}

const EMPTY_DRAFT = {
  activityKey: '',
  dimension: 'roots' as DimKey,
  rung: 'explorer' as Rung,
  displayTitle: '',
  displayHint: '',
  emissionBinding: '', // '' == "none — draft"
};

/**
 * This page uses shared dark-theme tokens (var(--text-primary), etc.), but the
 * admin console renders on a LIGHT background — so without overrides the text is
 * near-white on near-white and unreadable (Skip caught it 2026-07-22).
 *
 * Fix per CLAUDE.md "Theme-aware components in mixed-theme contexts": override
 * the tokens at the wrapper, don't touch the shared component. Mirrors
 * AdminGeographies.tsx's LIGHT_THEME_OVERRIDES, extended to cover the four
 * semantic colors this page also uses (success/warning/error/info).
 */
const LIGHT_THEME_OVERRIDES: React.CSSProperties = {
  // @ts-ignore — CSS custom properties are valid inline styles at runtime
  '--text-primary': '#020617',
  '--text-secondary': '#334155',
  '--text-muted': '#64748b',
  '--bg-primary': '#ffffff',
  '--bg-elevated': '#f8fafc',
  '--bg-surface': 'rgba(2, 6, 23, 0.04)',
  '--border-default': 'rgba(2, 6, 23, 0.15)',
  '--border-subtle': 'rgba(2, 6, 23, 0.08)',
  '--accent-primary': '#2563eb',
  '--accent-border': 'rgba(37, 99, 235, 0.40)',
  '--color-success': '#16a34a',
  '--color-warning': '#d97706',
  '--color-error': '#dc2626',
  '--info-text': '#2563eb',
} as React.CSSProperties;

interface CatalogResponse {
  activities: AdminActivity[];
  /** The fact detectors ImpactEmission actually implements. */
  knownBindings: string[];
}

/* ── Constants ── */

const DIMENSIONS: DimKey[] = ['mind', 'voice', 'body', 'heart', 'wings', 'roots'];

/** 'champion', not 'leader' — Skip's ruling 2026-07-13, superseding DEV-1301. */
const RUNGS: Rung[] = ['explorer', 'contributor', 'champion', 'governor'];

const DIM_LABEL: Record<DimKey, string> = {
  mind: 'Mind', voice: 'Voice', body: 'Body',
  heart: 'Heart', wings: 'Wings', roots: 'Roots',
};

const DIM_COLOR: Record<DimKey, string> = {
  mind: 'var(--dim-mind, #7ea3cc)',
  voice: 'var(--dim-voice, #5aabb5)',
  body: 'var(--dim-body, #6aab7a)',
  heart: 'var(--dim-heart, #c77a6f)',
  wings: 'var(--dim-wings, #c9a24e)',
  roots: 'var(--dim-roots, #8B7355)',
};

/** The member-facing catalog cache key — invalidated so the radar updates live. */
const MEMBER_CACHE_KEY = 'impactActivities:enabled';

/* ── Component ── */

export default function AdminImpactActivities() {
  const [activities, setActivities] = useState<AdminActivity[]>([]);
  const [knownBindings, setKnownBindings] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [rowError, setRowError] = useState<Record<string, string>>({});
  const [showOnlyWired, setShowOnlyWired] = useState(false);

  // DEV-1489 — create form + delete state.
  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState({ ...EMPTY_DRAFT });
  const [createErr, setCreateErr] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  // DEV-1489 (per-row edit) — set the binding, rung, copy, weights on an
  // existing row. This is what turns a seeded draft into a live act: set a real
  // detector here, save, and the On toggle becomes available.
  const [editing, setEditing] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<{
    emissionBinding: string; rung: Rung; repeatPolicy: string;
    magnitude: string; displayTitle: string; displayHint: string; dimensionWeights: string;
  } | null>(null);
  const [editErr, setEditErr] = useState<string | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch<CatalogResponse>('/api/admin/impact-activities');
      setActivities(res.activities);
      setKnownBindings(res.knownBindings);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load the activity catalog');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = useCallback(async (a: AdminActivity) => {
    setSaving(a.activityKey);
    setRowError((prev) => {
      const next = { ...prev };
      delete next[a.activityKey];
      return next;
    });
    try {
      const updated = await apiFetch<AdminActivity>(
        `/api/admin/impact-activities/${encodeURIComponent(a.activityKey)}`,
        { method: 'PATCH', body: JSON.stringify({ enabled: !a.enabled }) },
      );
      setActivities((prev) =>
        prev.map((x) => (x.activityKey === updated.activityKey ? updated : x)));

      // The member-facing radar reads the same catalog. Drop its cache so the
      // prompt list and the emission engine never disagree, even for a moment.
      invalidate(MEMBER_CACHE_KEY);
    } catch (e) {
      // The server returns a structured reason (unwired, bad binding). Show it —
      // a generic "could not save" would be a defect when we know the real cause.
      setRowError((prev) => ({
        ...prev,
        [a.activityKey]: e instanceof Error ? e.message : 'Could not update this activity',
      }));
    } finally {
      setSaving(null);
    }
  }, []);

  const createActivity = useCallback(async () => {
    setCreating(true);
    setCreateErr(null);
    try {
      await apiFetch<AdminActivity>('/api/admin/impact-activities', {
        method: 'POST',
        body: JSON.stringify({
          activityKey: draft.activityKey.trim(),
          dimension: draft.dimension,
          rung: draft.rung,
          displayTitle: draft.displayTitle.trim(),
          displayHint: draft.displayHint.trim() || null,
          // '' means "none — draft": born unwired and un-enableable, on purpose.
          emissionBinding: draft.emissionBinding || null,
        }),
      });
      setDraft({ ...EMPTY_DRAFT });
      setShowCreate(false);
      invalidate(MEMBER_CACHE_KEY);
      await load();
    } catch (e) {
      // The server returns a structured reason (bad key shape, dup, unknown
      // binding). Surface it — a generic failure would be a defect here.
      setCreateErr(e instanceof Error ? e.message : 'Could not create the activity');
    } finally {
      setCreating(false);
    }
  }, [draft, load]);

  const startEdit = useCallback((a: AdminActivity) => {
    setEditing(a.activityKey);
    setEditErr(null);
    setEditDraft({
      emissionBinding: a.emissionBinding ?? '',   // '' = none — draft
      rung: a.rung,
      repeatPolicy: a.repeatPolicy,
      magnitude: String(a.magnitude),
      displayTitle: a.displayTitle,
      displayHint: a.displayHint ?? '',
      dimensionWeights: a.dimensionWeights,
    });
  }, []);

  const cancelEdit = useCallback(() => {
    setEditing(null);
    setEditDraft(null);
    setEditErr(null);
  }, []);

  const saveEdit = useCallback(async (a: AdminActivity) => {
    if (!editDraft) return;
    setSavingEdit(true);
    setEditErr(null);
    try {
      const mag = Number(editDraft.magnitude);
      if (!Number.isFinite(mag) || mag <= 0) {
        setEditErr('Magnitude must be a number greater than 0.');
        setSavingEdit(false);
        return;
      }
      const updated = await apiFetch<AdminActivity>(
        `/api/admin/impact-activities/${encodeURIComponent(a.activityKey)}`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            // '' clears the binding (draft); a key sets it. The dropdown is
            // authoritative, so we always send a string, never null-unchanged.
            emissionBinding: editDraft.emissionBinding,
            rung: editDraft.rung,
            repeatPolicy: editDraft.repeatPolicy,
            magnitude: mag,
            displayTitle: editDraft.displayTitle.trim(),
            displayHint: editDraft.displayHint.trim() || null,
            dimensionWeights: editDraft.dimensionWeights.trim() || undefined,
          }),
        },
      );
      setActivities((prev) =>
        prev.map((x) => (x.activityKey === updated.activityKey
          // preserve hasEmitted (PATCH response doesn't recompute it)
          ? { ...updated, hasEmitted: x.hasEmitted }
          : x)));
      invalidate(MEMBER_CACHE_KEY);
      cancelEdit();
    } catch (e) {
      // Structured server reasons: unknown binding, "disable before unbinding",
      // duplicate binding (unique), bad JSON weights. Show verbatim.
      setEditErr(e instanceof Error ? e.message : 'Could not save this activity');
    } finally {
      setSavingEdit(false);
    }
  }, [editDraft, cancelEdit]);

  const deleteActivity = useCallback(async (a: AdminActivity) => {
    setDeleting(a.activityKey);
    setRowError((prev) => {
      const next = { ...prev };
      delete next[a.activityKey];
      return next;
    });
    try {
      await apiFetch(
        `/api/admin/impact-activities/${encodeURIComponent(a.activityKey)}`,
        { method: 'DELETE' },
      );
      invalidate(MEMBER_CACHE_KEY);
      await load();
    } catch (e) {
      // A 409 here is the disable-and-explain refusal (emitted / seeded). Show
      // the server's reason verbatim.
      setRowError((prev) => ({
        ...prev,
        [a.activityKey]: e instanceof Error ? e.message : 'Could not delete this activity',
      }));
    } finally {
      setDeleting(null);
    }
  }, [load]);

  const stats = useMemo(() => ({
    total: activities.length,
    enabled: activities.filter((a) => a.enabled).length,
    wired: activities.filter((a) => a.isWired).length,
  }), [activities]);

  /** dimension → rung → rows, so the grid mirrors the 6×4 model. */
  const grid = useMemo(() => {
    const src = showOnlyWired ? activities.filter((a) => a.isWired) : activities;
    const out = {} as Record<DimKey, Record<Rung, AdminActivity[]>>;
    for (const d of DIMENSIONS) {
      out[d] = { explorer: [], contributor: [], champion: [], governor: [] };
    }
    for (const a of src) {
      if (out[a.dimension]?.[a.rung]) out[a.dimension][a.rung].push(a);
    }
    return out;
  }, [activities, showOnlyWired]);

  if (loading) return <div className={styles.page} style={LIGHT_THEME_OVERRIDES}><p>Loading the activity catalog…</p></div>;
  if (error) return <div className={styles.page} style={LIGHT_THEME_OVERRIDES}><p className={styles.error}>{error}</p></div>;

  return (
    <div className={styles.page} style={LIGHT_THEME_OVERRIDES}>
      <header className={styles.header}>
        <h1>Impact Activities</h1>
        <p className={styles.subtitle}>
          The full catalog of acts a member can take, across six dimensions and four rungs.
          Enabling an activity makes it earn credit <em>and</em> surfaces it in the member's
          radar prompts — the same rows drive both, so a prompt can never advertise credit
          the engine won't give.
        </p>

        <div className={styles.stats}>
          <span><strong>{stats.total}</strong> activities</span>
          <span><strong>{stats.enabled}</strong> enabled</span>
          <span><strong>{stats.wired}</strong> wired</span>
          <span className={styles.statMuted}>
            <strong>{stats.total - stats.wired}</strong> awaiting a fact detector
          </span>
        </div>

        {/* The honest headline: most of this catalog is aspiration, not capability. */}
        {stats.wired < stats.total && (
          <div className={styles.notice}>
            <strong>{stats.total - stats.wired} activities cannot be enabled yet.</strong>{' '}
            They were imported from the activity catalog spreadsheet as drafts, but no code
            exists that can detect them happening. An activity only becomes enableable once a
            fact detector is written for it in <code>ImpactEmission</code> and registered as an
            emission binding. Until then its toggle stays disabled — deliberately, so the
            platform can never promise a member credit it has no way to award.
          </div>
        )}

        <label className={styles.filterToggle}>
          <input
            type="checkbox"
            checked={showOnlyWired}
            onChange={(e) => setShowOnlyWired(e.target.checked)}
          />
          Show only wired activities ({stats.wired})
        </label>

        {/* ── DEV-1489 — create an admin-authored activity ──
            It is born a SAFE DRAFT: disabled, and (unless a real detector is
            picked here) unwired — so it cannot emit and cannot be enabled until a
            binding is set. Enabling stays blocked by the same constraint that
            guards every row. */}
        <div className={styles.createBar}>
          <button
            type="button"
            className={styles.createToggle}
            onClick={() => { setShowCreate((v) => !v); setCreateErr(null); }}
          >
            {showCreate ? 'Cancel' : '+ New activity'}
          </button>
        </div>

        {showCreate && (
          <div className={styles.createForm}>
            <p className={styles.createNote}>
              A new activity is born disabled and, unless you pick a detector below,
              unwired — it cannot emit or be enabled until a fact detector backs it.
              Leave the binding as <em>none — draft</em> if the detector doesn't
              exist yet.
            </p>
            <div className={styles.createGrid}>
              <label>
                Dimension
                <select
                  value={draft.dimension}
                  onChange={(e) => setDraft((d) => ({ ...d, dimension: e.target.value as DimKey }))}
                >
                  {DIMENSIONS.map((dim) => (
                    <option key={dim} value={dim}>{DIM_LABEL[dim]}</option>
                  ))}
                </select>
              </label>
              <label>
                Rung
                <select
                  value={draft.rung}
                  onChange={(e) => setDraft((d) => ({ ...d, rung: e.target.value as Rung }))}
                >
                  {RUNGS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </label>
              <label className={styles.createKey}>
                Activity key
                <input
                  type="text"
                  placeholder="e.g. wings.first_donation"
                  value={draft.activityKey}
                  onChange={(e) => setDraft((d) => ({ ...d, activityKey: e.target.value }))}
                />
              </label>
              <label className={styles.createTitle}>
                Title (member-facing)
                <input
                  type="text"
                  placeholder="e.g. Make your first donation"
                  value={draft.displayTitle}
                  onChange={(e) => setDraft((d) => ({ ...d, displayTitle: e.target.value }))}
                />
              </label>
              <label className={styles.createHint}>
                Hint (optional)
                <input
                  type="text"
                  value={draft.displayHint}
                  onChange={(e) => setDraft((d) => ({ ...d, displayHint: e.target.value }))}
                />
              </label>
              <label>
                Emission binding
                <select
                  value={draft.emissionBinding}
                  onChange={(e) => setDraft((d) => ({ ...d, emissionBinding: e.target.value }))}
                >
                  <option value="">none — draft</option>
                  {knownBindings.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </label>
            </div>
            {createErr && <div className={styles.rowError}>{createErr}</div>}
            <div className={styles.createActions}>
              <button
                type="button"
                className={styles.createSubmit}
                disabled={creating || !draft.activityKey.trim() || !draft.displayTitle.trim()}
                onClick={createActivity}
              >
                {creating ? 'Creating…' : 'Create draft'}
              </button>
            </div>
          </div>
        )}
      </header>

      {DIMENSIONS.map((dim) => {
        const rungs = grid[dim];
        const count = RUNGS.reduce((n, r) => n + rungs[r].length, 0);
        if (count === 0) return null;

        const enabledInDim = RUNGS.reduce(
          (n, r) => n + rungs[r].filter((a) => a.enabled).length, 0);

        return (
          <section key={dim} className={styles.dimension}>
            <h2 className={styles.dimHeading} style={{ borderInlineStartColor: DIM_COLOR[dim] }}>
              <span style={{ color: DIM_COLOR[dim] }}>{DIM_LABEL[dim]}</span>
              <span className={styles.dimCount}>
                {enabledInDim} of {count} enabled
              </span>
              {/* A dimension with nothing enabled shows members its concept copy and NO
                  growth hints. Say so here, so the emptiness reads as a known state
                  rather than a bug. */}
              {enabledInDim === 0 && (
                <span className={styles.dimEmpty}>
                  no growth hints shown to members
                </span>
              )}
            </h2>

            {RUNGS.map((rung) => {
              const rows = rungs[rung];
              if (rows.length === 0) return null;
              return (
                <div key={rung} className={styles.rungBlock}>
                  <h3 className={styles.rungHeading}>{rung}</h3>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th className={styles.colToggle}>On</th>
                        <th>Activity</th>
                        <th className={styles.colBinding}>Emission binding</th>
                        <th className={styles.colPolicy}>Repeat</th>
                        <th className={styles.colOrigin}>Origin</th>
                        <th className={styles.colAudit}>Last toggled</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((a) => {
                        const busy = saving === a.activityKey;
                        const err = rowError[a.activityKey];
                        const isEditing = editing === a.activityKey;
                        return (
                          <React.Fragment key={a.activityKey}>
                          <tr
                            className={a.enabled ? styles.rowEnabled : undefined}
                          >
                            <td className={styles.colToggle}>
                              {/* Disable-and-explain. An unwired activity's toggle is
                                  inert and says why — never clickable-then-broken. */}
                              <input
                                type="checkbox"
                                checked={a.enabled}
                                disabled={!a.isWired || busy}
                                onChange={() => toggle(a)}
                                title={
                                  a.isWired
                                    ? (a.enabled ? 'Disable (stops future credit; history kept)' : 'Enable')
                                    : 'Not yet wired — needs a fact detector in ImpactEmission before it can be enabled'
                                }
                              />
                            </td>

                            <td>
                              <div className={styles.activityTitle}>{a.displayTitle}</div>
                              {a.displayHint && (
                                <div className={styles.activityHint}>{a.displayHint}</div>
                              )}
                              <code className={styles.activityKey}>{a.activityKey}</code>

                              {/* DEV-1489 — Edit opens the per-row editor: set the
                                  binding (turns a draft into a live act), rung, copy,
                                  weights. */}
                              <button
                                type="button"
                                className={styles.editBtn}
                                onClick={() => (isEditing ? cancelEdit() : startEdit(a))}
                              >
                                {isEditing ? 'Close' : 'Edit'}
                              </button>

                              {/* DEV-1489 — delete affordance, disable-and-explain.
                                  Only a never-emitted admin draft is hard-deletable;
                                  a seeded or already-emitted row is disable-only
                                  (medals stay earned), and its control says why
                                  rather than failing on click. */}
                              {isHardDeletable(a) ? (
                                <button
                                  type="button"
                                  className={styles.deleteBtn}
                                  disabled={deleting === a.activityKey}
                                  onClick={() => deleteActivity(a)}
                                  title="Delete this draft. It has never emitted, so nothing is lost."
                                >
                                  {deleting === a.activityKey ? 'Deleting…' : 'Delete draft'}
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className={styles.deleteBtnDisabled}
                                  disabled
                                  title={
                                    a.hasEmitted
                                      ? 'Already earned by members — disable it instead (medals stay earned)'
                                      : 'Seeded with the platform — disable it instead'
                                  }
                                >
                                  {a.hasEmitted ? 'Earned — disable only' : 'Seeded — disable only'}
                                </button>
                              )}

                              {err && <div className={styles.rowError}>{err}</div>}
                            </td>

                            <td className={styles.colBinding}>
                              {a.isWired ? (
                                <code className={styles.binding}>{a.emissionBinding}</code>
                              ) : (
                                <span className={styles.unwired} title={
                                  `No code can detect this act. Detectors available: ${knownBindings.join(', ')}`
                                }>
                                  not yet wired
                                </span>
                              )}
                            </td>

                            <td className={styles.colPolicy}>
                              <span className={styles.policy}>{a.repeatPolicy}</span>
                            </td>

                            <td className={styles.colOrigin}>
                              <span className={a.isSeeded ? styles.seeded : styles.custom}>
                                {a.isSeeded ? 'seeded' : 'custom'}
                              </span>
                              {a.sourceNote && (
                                <div className={styles.sourceNote} title={a.sourceNote}>
                                  {a.sourceNote}
                                </div>
                              )}
                            </td>

                            <td className={styles.colAudit}>
                              {a.enabledChangedAt ? (
                                <span title={a.enabledChangedByUserGuid || ''}>
                                  {new Date(a.enabledChangedAt).toLocaleString()}
                                </span>
                              ) : (
                                <span className={styles.never}>—</span>
                              )}
                            </td>
                          </tr>

                          {/* ── DEV-1489 — per-row editor ──
                              Set the emission binding (the piece that turns a
                              seeded draft into a live act), plus rung, copy,
                              repeat policy, magnitude, weights. Saving via PATCH;
                              the server enforces the cap rules and surfaces a real
                              reason (unknown binding, "disable before unbinding",
                              duplicate binding) rather than a generic failure. */}
                          {isEditing && editDraft && (
                            <tr className={styles.editorRow}>
                              <td colSpan={6}>
                                <div className={styles.editor}>
                                  <div className={styles.editGrid}>
                                    <label>
                                      Emission binding
                                      <select
                                        value={editDraft.emissionBinding}
                                        onChange={(e) => setEditDraft((d) => d && ({ ...d, emissionBinding: e.target.value }))}
                                      >
                                        <option value="">none — draft</option>
                                        {knownBindings.map((b) => <option key={b} value={b}>{b}</option>)}
                                      </select>
                                    </label>
                                    <label>
                                      Rung
                                      <select
                                        value={editDraft.rung}
                                        onChange={(e) => setEditDraft((d) => d && ({ ...d, rung: e.target.value as Rung }))}
                                      >
                                        {RUNGS.map((r) => <option key={r} value={r}>{r}</option>)}
                                      </select>
                                    </label>
                                    <label>
                                      Repeat policy
                                      <select
                                        value={editDraft.repeatPolicy}
                                        onChange={(e) => setEditDraft((d) => d && ({ ...d, repeatPolicy: e.target.value }))}
                                      >
                                        <option value="once_per_user">once_per_user</option>
                                        <option value="once_per_source">once_per_source</option>
                                        <option value="repeatable">repeatable</option>
                                      </select>
                                    </label>
                                    <label>
                                      Magnitude
                                      <input
                                        type="number"
                                        step="0.1"
                                        min="0.1"
                                        value={editDraft.magnitude}
                                        onChange={(e) => setEditDraft((d) => d && ({ ...d, magnitude: e.target.value }))}
                                      />
                                    </label>
                                    <label className={styles.editWide}>
                                      Title (member-facing)
                                      <input
                                        type="text"
                                        value={editDraft.displayTitle}
                                        onChange={(e) => setEditDraft((d) => d && ({ ...d, displayTitle: e.target.value }))}
                                      />
                                    </label>
                                    <label className={styles.editWide}>
                                      Hint
                                      <input
                                        type="text"
                                        value={editDraft.displayHint}
                                        onChange={(e) => setEditDraft((d) => d && ({ ...d, displayHint: e.target.value }))}
                                      />
                                    </label>
                                    <label className={styles.editWide}>
                                      Dimension weights (JSON)
                                      <input
                                        type="text"
                                        value={editDraft.dimensionWeights}
                                        onChange={(e) => setEditDraft((d) => d && ({ ...d, dimensionWeights: e.target.value }))}
                                        title='e.g. {"roots": 1.0}'
                                      />
                                    </label>
                                  </div>
                                  {editErr && <div className={styles.rowError}>{editErr}</div>}
                                  <div className={styles.editActions}>
                                    <button
                                      type="button"
                                      className={styles.createSubmit}
                                      disabled={savingEdit || !editDraft.displayTitle.trim()}
                                      onClick={() => saveEdit(a)}
                                    >
                                      {savingEdit ? 'Saving…' : 'Save'}
                                    </button>
                                    <button type="button" className={styles.editCancel} onClick={cancelEdit}>
                                      Cancel
                                    </button>
                                    {!a.isWired && (
                                      <span className={styles.editHelp}>
                                        Set a binding, save, then use the On toggle to enable it.
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </section>
        );
      })}
    </div>
  );
}
